import { useEffect, useState } from 'react';
import { supabase, fullNameLastFirst } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';

const ROLES = ['admin', 'manager', 'coordinator', 'nurse', 'staff'];


// Fallback for roles with no matching entry in the live `permissionDefaults`
// (e.g. table hasn't loaded yet).
const FALLBACK_DEFAULT_MODULES = ['hh'];

const MODULES = [
  { key: 'hh',      label: 'Home Health',    icon: '🏥', desc: 'Pipeline, schedule, visits, clinical' },
  { key: 'dme',     label: 'HME / DME',      icon: '📦', desc: 'Orders, inventory, delivery, CMN' },
  { key: 'finance', label: 'Finance',        icon: '💰', desc: 'Revenue, payroll, claims' },
  { key: 'hr',      label: 'HR & Caregivers',icon: '👥', desc: 'Staff records' },
  { key: 'quality', label: 'Quality Mgmt',   icon: '🛡️', desc: 'QA surveys, complaints, incidents, policies' },
];

const roleColor = (role) => ({
  admin:       { bg: 'var(--danger-bg)', color: 'var(--danger)' },
  manager:     { bg: 'var(--copper-bg)', color: 'var(--copper)' },
  coordinator: { bg: 'var(--accent-bg)', color: 'var(--accent2)' },
  nurse:       { bg: 'var(--success-bg)', color: 'var(--success)' },
  staff:       { bg: 'var(--bg2)', color: 'var(--text3)' },
}[role] || { bg: 'var(--bg2)', color: 'var(--text3)' });

const invokeStaffFunction = async (body) => {
  const { data, error } = await supabase.functions.invoke('invite-staff', { body });
  if (error) throw new Error(error.message);
  if (data?.error) throw new Error(data.error);
  return data;
};

export default function Staff() {
  const { profile, permissionDefaults } = useAuth();
  const defaultModulesForRole = (role) => {
    const defaults = permissionDefaults?.[role];
    if (!defaults) return FALLBACK_DEFAULT_MODULES;
    return Object.keys(defaults).filter(mod => defaults[mod]?.can_view);
  };
  const [staff,           setStaff]           = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState('');
  const [success,         setSuccess]         = useState('');
  const [saving,          setSaving]          = useState(false);

  // Modal states
  const [showInvite,      setShowInvite]      = useState(false);
  const [showEdit,        setShowEdit]        = useState(false);
  const [showReset,       setShowReset]       = useState(false);
  const [showChangePw,    setShowChangePw]    = useState(false);
  const [editingMember,   setEditingMember]   = useState(null);

  // Form states
  const [inviteForm,      setInviteForm]      = useState({ full_name: '', email: '', role: 'coordinator' });
  const [editForm,        setEditForm]        = useState({ full_name: '', role: 'coordinator' });
  const [newPassword,     setNewPassword]     = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw,          setShowPw]          = useState(false);
  const [resetResult,     setResetResult]     = useState(null);
  const [resetMode,       setResetMode]       = useState('generate'); // 'generate' | 'manual'
  const [adminPw,         setAdminPw]         = useState('');
  const [adminPw2,        setAdminPw2]        = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    const [{ data }, { data: linkedStaff }] = await Promise.all([
      supabase.from('profiles').select('id, full_name, email, role, active, modules').order('full_name'),
      supabase.from('staff').select('id, profile_id').not('profile_id', 'is', null),
    ]);
    const staffIdByProfileId = {};
    (linkedStaff || []).forEach(s => { staffIdByProfileId[s.profile_id] = s.id; });
    setStaff((data || []).map(p => ({ ...p, linked_staff_id: staffIdByProfileId[p.id] || null })));
    setLoading(false);
  };

  const toast = (msg, isError = false) => {
    if (isError) setError(msg); else setSuccess(msg);
    setTimeout(() => { setError(''); setSuccess(''); }, 4000);
  };

  // ── Invite new user ────────────────────────────────────────────────────────
  const handleInvite = async () => {
    if (!inviteForm.full_name || !inviteForm.email) { setError('Name and email are required'); return; }
    setSaving(true); setError('');
    try {
      const result = await invokeStaffFunction({ action: 'invite', ...inviteForm });
      setShowInvite(false);
      setInviteForm({ full_name: '', email: '', role: 'coordinator' });
      await load();
      toast(`${inviteForm.full_name} added. Temporary password sent to ${inviteForm.email}`);
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  // ── Edit user ──────────────────────────────────────────────────────────────
  const openEdit = (member) => {
    setEditingMember(member);
    setEditForm({
      full_name: member.full_name || '',
      role:      member.role || 'coordinator',
    });
    setError('');
    setShowEdit(true);
  };

  const handleEdit = async () => {
    setSaving(true); setError('');
    const { error: err } = await supabase.from('profiles').update({
      full_name: editForm.full_name,
      role:      editForm.role,
    }).eq('id', editingMember.id);
    if (err) { setError(err.message); setSaving(false); return; }
    await load();
    setShowEdit(false);
    toast(`${editForm.full_name} updated`);
    setSaving(false);
  };

  // ── Reset password (admin resets for another user) ─────────────────────────
  const openReset = (member) => {
    setEditingMember(member);
    setResetResult(null);
    setResetMode('generate');
    setAdminPw('');
    setAdminPw2('');
    setError('');
    setShowReset(true);
  };

  const handleReset = async () => {
    if (resetMode === 'manual') {
      if (adminPw.length < 8)   { setError('Password must be at least 8 characters'); return; }
      if (adminPw !== adminPw2) { setError('Passwords do not match'); return; }
    }
    setSaving(true); setError('');
    try {
      if (resetMode === 'manual') {
        await invokeStaffFunction({ action: 'change_password', user_id: editingMember.id, password: adminPw });
        setResetResult({ manual: true });
      } else {
        const result = await invokeStaffFunction({
          action:  'reset_password',
          user_id: editingMember.id,
          email:   editingMember.email,
        });
        setResetResult({ tempPassword: result.tempPassword });
      }
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  // ── Change own password ────────────────────────────────────────────────────
  const openChangePw = () => {
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setShowChangePw(true);
  };

  const handleChangePw = async () => {
    if (newPassword.length < 8)          { setError('Password must be at least 8 characters'); return; }
    if (newPassword !== confirmPassword)  { setError('Passwords do not match'); return; }
    setSaving(true); setError('');
    try {
      await invokeStaffFunction({ action: 'change_password', user_id: profile.id, password: newPassword });
      setShowChangePw(false);
      toast('Password changed successfully');
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  // ── Toggle active ──────────────────────────────────────────────────────────
  const handleToggleActive = async (member) => {
    if (member.id === profile.id) return;
    const newActive = !member.active;
    const { error: err } = await supabase.from('profiles').update({ active: newActive }).eq('id', member.id);
    if (err) { toast(err.message, true); return; }
    setStaff(prev => prev.map(s => s.id === member.id ? { ...s, active: newActive } : s));
    toast(`${member.full_name} ${newActive ? 'activated' : 'deactivated'}`);
  };

  // ── Shared modal wrapper ──────────────────────────────────────────────────

  const columns = [
    {
      id: 'name', header: 'Name', size: 200,
      meta: { mobile: 'primary' },
      cell: info => {
        const member = info.row.original;
        const isMe = member.id === profile.id;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--accent)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600, flexShrink: 0 }}>
              {member.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?'}
            </div>
            <div>
              <span style={{ fontWeight: 500, color: 'var(--text)' }}>{fullNameLastFirst(member.full_name)}</span>
              {isMe && <span style={{ fontSize: 10, background: 'var(--accent-bg)', color: 'var(--accent2)', padding: '1px 6px', borderRadius: 10, marginLeft: 6 }}>You</span>}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'email', header: 'Email', size: 210,
      meta: { mobile: 'secondary', truncate: true },
      cell: info => info.getValue(),
    },
    {
      accessorKey: 'role', header: 'Role', size: 120,
      meta: { mobile: 'secondary' },
      cell: info => {
        const rc = roleColor(info.getValue());
        return <span style={{ fontSize: 12, fontWeight: 500, padding: '3px 10px', borderRadius: 20, background: rc.bg, color: rc.color, textTransform: 'capitalize' }}>{info.getValue()}</span>;
      },
    },
    {
      id: 'modules', header: 'Modules', size: 200, enableSorting: false,
      meta: { mobile: 'hidden' },
      cell: info => {
        const member = info.row.original;
        const mods = member.modules || defaultModulesForRole(member.role);
        return (
          <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {mods.map(m => {
              const mod = MODULES.find(x => x.key === m);
              return mod ? <span key={m} style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'var(--bg2)', color: 'var(--text3)', border: '0.5px solid var(--border)' }}>{mod.icon} {mod.label}</span> : null;
            })}
            {mods.length === 0 && <span style={{ fontSize: 11, color: 'var(--text3)' }}>—</span>}
          </div>
        );
      },
    },
    {
      accessorKey: 'active', header: 'Status', size: 100,
      meta: { mobile: 'primary' },
      cell: info => (
        <span style={{ fontSize: 11, fontWeight: 500, padding: '3px 10px', borderRadius: 20, background: info.getValue() ? 'var(--success-bg)' : 'var(--bg2)', color: info.getValue() ? 'var(--success)' : 'var(--text3)' }}>
          {info.getValue() ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      id: 'actions', header: 'Actions', size: 240, enableSorting: false,
      meta: { mobile: 'secondary' },
      cell: info => {
        const member = info.row.original;
        const isMe = member.id === profile.id;
        return (
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            <button className="btn btn-outline btn-sm" style={{ fontSize: 11 }} onClick={() => openEdit(member)}>Edit</button>
            {!isMe && <button className="btn btn-outline btn-sm" style={{ fontSize: 11, color: 'var(--warn)', borderColor: 'var(--warn-border)' }} onClick={() => openReset(member)}>Reset PW</button>}
            {!isMe && (
              <button className="btn btn-outline btn-sm" style={{ fontSize: 11, color: member.active ? 'var(--danger)' : 'var(--success)', borderColor: member.active ? 'var(--danger-border)' : 'var(--success-border)' }} onClick={() => handleToggleActive(member)}>
                {member.active ? 'Deactivate' : 'Activate'}
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div>
      {success && <div style={{ background:'var(--success-bg)', border:'0.5px solid var(--success-border)', borderRadius:8, padding:'10px 14px', marginBottom:12, fontSize:12, color:'var(--success)' }}>✓ {success}</div>}
      {error && !showInvite && !showEdit && !showReset && !showChangePw && (
        <div style={{ background:'var(--danger-bg)', border:'0.5px solid var(--danger-border)', borderRadius:8, padding:'10px 14px', marginBottom:12, fontSize:12, color:'var(--danger)' }}>⚠ {error}</div>
      )}

      <div className="card">
        <div className="card-header">
          <div className="card-title">User Management ({staff.length})</div>
          <div style={{ display:'flex', gap:8 }}>
            <button className="btn btn-outline btn-sm" onClick={openChangePw}>🔑 Change My Password</button>
            <button className="btn btn-primary btn-sm" onClick={() => { setShowInvite(true); setError(''); }}>+ Add User</button>
          </div>
        </div>

        <DataTable
          tableId="staff-users"
          columns={columns}
          data={staff}
          getRowId={s => s.id}
          loading={loading}
          emptyMessage="No staff accounts yet."
          maxHeight="calc(100vh - 240px)"
        />
      </div>

      {/* ── Invite Modal ── */}
      <Modal show={showInvite} onClose={() => setShowInvite(false)} title="Add New User" error={error}
        footer={<>
          <button className="btn btn-outline" onClick={() => setShowInvite(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleInvite} disabled={saving}>{saving ? 'Sending...' : 'Add User'}</button>
        </>}>
        <div style={{ background:'var(--accent-bg)', border:'0.5px solid var(--accent-border)', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:12, color:'var(--accent2)' }}>
          A temporary password will be generated and emailed to the user. They can change it after logging in.
        </div>
        <div className="form-group">
          <label className="form-label">Full Name *</label>
          <input className="form-input" value={inviteForm.full_name} onChange={e => setInviteForm(p=>({...p,full_name:e.target.value}))} placeholder="e.g. Brittany Thompson" />
        </div>
        <div className="form-group">
          <label className="form-label">Email Address *</label>
          <input type="email" className="form-input" value={inviteForm.email} onChange={e => setInviteForm(p=>({...p,email:e.target.value}))} placeholder="e.g. brittany@aheartforcare.com" />
        </div>
        <div className="form-group">
          <label className="form-label">Role</label>
          <select className="form-select" value={inviteForm.role} onChange={e => setInviteForm(p=>({...p,role:e.target.value}))}>
            {ROLES.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase()+r.slice(1)}</option>)}
          </select>
          <div className="form-hint">
            Admin — full access · Manager — all modules + finance · Coordinator — HH + HR · Nurse — HH only · Staff — HH view only
          </div>
        </div>
      </Modal>

      {/* ── Edit Modal ── */}
      <Modal show={showEdit} onClose={() => setShowEdit(false)} title={`Edit — ${fullNameLastFirst(editingMember?.full_name)}`} error={error}
        footer={<>
          <button className="btn btn-outline" onClick={() => setShowEdit(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleEdit} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
        </>}>
        <div className="form-group">
          <label className="form-label">Full Name</label>
          <input className="form-input" value={editForm.full_name} onChange={e => setEditForm(p=>({...p,full_name:e.target.value}))} disabled={!!editingMember?.linked_staff_id} style={editingMember?.linked_staff_id ? { opacity:0.5 } : undefined} />
          {editingMember?.linked_staff_id && (
            <div className="form-hint">This person has an HR personnel record — edit their name in Personnel instead, it stays in sync with their login automatically.</div>
          )}
        </div>
        <div className="form-group">
          <label className="form-label">Email</label>
          <input className="form-input" value={editingMember?.email || ''} disabled style={{ opacity:0.5 }} />
          <div className="form-hint">Email cannot be changed here</div>
        </div>
        <div className="form-group">
          <label className="form-label">Role</label>
          <select className="form-select" value={editForm.role} onChange={e => setEditForm(p=>({...p,role:e.target.value}))}>
            {ROLES.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase()+r.slice(1)}</option>)}
          </select>
        </div>
        <div className="form-hint">
          Module access is managed in Settings → Access Control → Individual Overrides.
        </div>
      </Modal>

      {/* ── Reset Password Modal ── */}
      <Modal show={showReset} onClose={() => { setShowReset(false); setResetResult(null); }} title={`Reset Password — ${fullNameLastFirst(editingMember?.full_name)}`} error={error}
        footer={!resetResult ? <>
          <button className="btn btn-outline" onClick={() => setShowReset(false)}>Cancel</button>
          <button className="btn btn-primary" style={{ background:'var(--warn)', borderColor:'var(--warn)' }} onClick={handleReset} disabled={saving}>{saving ? 'Saving...' : resetMode === 'manual' ? 'Set Password' : 'Reset Password'}</button>
        </> : <button className="btn btn-primary" onClick={() => { setShowReset(false); setResetResult(null); }}>Done</button>}>
        {!resetResult ? (
          <>
            <div style={{ display:'flex', gap:8, marginBottom:16 }}>
              {[
                { key:'generate', label:'Email a temporary password' },
                { key:'manual',   label:'Set a specific password' },
              ].map(opt => (
                <button key={opt.key} className="btn btn-sm" style={{ flex:1, fontSize:12,
                  background: resetMode === opt.key ? 'var(--accent)' : 'var(--bg2)',
                  color: resetMode === opt.key ? '#fff' : 'var(--text3)',
                  border: '0.5px solid var(--border)' }}
                  onClick={() => { setResetMode(opt.key); setError(''); }}>{opt.label}</button>
              ))}
            </div>

            {resetMode === 'generate' ? (
              <>
                <div style={{ background:'var(--warn-bg)', border:'0.5px solid var(--warn-border)', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:12, color:'var(--warn)' }}>
                  This will generate a new temporary password and email it to <strong>{editingMember?.email}</strong>. The user will need to change it after logging in.
                </div>
                <p style={{ fontSize:13, color:'var(--text2)' }}>Are you sure you want to reset the password for <strong>{editingMember?.full_name}</strong>?</p>
              </>
            ) : (
              <>
                <div style={{ background:'var(--warn-bg)', border:'0.5px solid var(--warn-border)', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:12, color:'var(--warn)' }}>
                  Sets <strong>{editingMember?.full_name}</strong>'s password immediately. No email is sent — you'll need to pass it on yourself, and they can change it later from the key icon in the sidebar.
                </div>
                <div className="form-group">
                  <label className="form-label">New Password</label>
                  <input type={showPw ? 'text' : 'password'} className="form-input" value={adminPw} onChange={e => setAdminPw(e.target.value)} placeholder="Minimum 8 characters" />
                </div>
                <div className="form-group">
                  <label className="form-label">Confirm Password</label>
                  <input type={showPw ? 'text' : 'password'} className="form-input" value={adminPw2} onChange={e => setAdminPw2(e.target.value)} placeholder="Repeat the password" />
                  {adminPw2 && adminPw !== adminPw2 && <div style={{ fontSize:11, color:'var(--danger)', marginTop:4 }}>Passwords do not match</div>}
                  {adminPw2 && adminPw === adminPw2 && <div style={{ fontSize:11, color:'var(--success)', marginTop:4 }}>✓ Passwords match</div>}
                </div>
                <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, color:'var(--text3)' }}>
                  <input type="checkbox" checked={showPw} onChange={e => setShowPw(e.target.checked)} /> Show password
                </label>
              </>
            )}
          </>
        ) : resetResult.manual ? (
          <div style={{ textAlign:'center', padding:'8px 0' }}>
            <div style={{ fontSize:32, marginBottom:12 }}>✓</div>
            <div style={{ fontSize:14, fontWeight:500, color:'var(--success)', marginBottom:8 }}>Password updated</div>
            <div style={{ fontSize:13, color:'var(--text2)' }}>
              <strong>{editingMember?.full_name}</strong> can sign in with the password you set. No email was sent — share it with them directly.
            </div>
          </div>
        ) : (
          <div style={{ textAlign:'center', padding:'8px 0' }}>
            <div style={{ fontSize:32, marginBottom:12 }}>✓</div>
            <div style={{ fontSize:14, fontWeight:500, color:'var(--success)', marginBottom:8 }}>Password reset successfully</div>
            <div style={{ fontSize:13, color:'var(--text2)', marginBottom:16 }}>
              An email has been sent to <strong>{editingMember?.email}</strong> with the temporary password.
            </div>
            <div style={{ background:'var(--bg2)', border:'0.5px solid var(--border)', borderRadius:8, padding:'12px 16px' }}>
              <div style={{ fontSize:11, color:'var(--text3)', marginBottom:6 }}>Temporary password (share if email didn't arrive):</div>
              <code style={{ fontSize:16, fontWeight:500, color:'var(--text)', background:'var(--bg3)', padding:'6px 14px', borderRadius:6, display:'block' }}>{resetResult.tempPassword}</code>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Change Own Password Modal ── */}
      <Modal show={showChangePw} onClose={() => setShowChangePw(false)} title="Change My Password" error={error}
        footer={<>
          <button className="btn btn-outline" onClick={() => setShowChangePw(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleChangePw} disabled={saving}>{saving ? 'Saving...' : 'Change Password'}</button>
        </>}>
        <div className="form-group">
          <label className="form-label">New Password</label>
          <div style={{ position:'relative' }}>
            <input type={showPw ? 'text' : 'password'} className="form-input" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Minimum 8 characters" style={{ paddingRight:40 }} />
            <button type="button" onClick={() => setShowPw(p=>!p)} style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'var(--text3)', padding:4 }}>
              {showPw ? '🙈' : '👁'}
            </button>
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Confirm New Password</label>
          <input type={showPw ? 'text' : 'password'} className="form-input" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Re-enter new password" />
          {confirmPassword && newPassword !== confirmPassword && (
            <div style={{ fontSize:11, color:'var(--danger)', marginTop:4 }}>Passwords do not match</div>
          )}
        </div>
        <div style={{ fontSize:12, color:'var(--text3)' }}>
          Password must be at least 8 characters. Use a mix of letters, numbers and symbols.
        </div>
      </Modal>
    </div>
  );
}
